import * as preact from "preact";

import readlangClient from "readlangClient";
import { cn } from "shared/utils";
import AdminFeedbackRatingCells, {
  type FeedbackRatings,
} from "views/AdminFeedbackRatingCells";

// Types for props
type Props = {
  appRouter: any;
};

// Types for state
type State = {
  feedback?: Feedback[];
  showResolved: boolean;
  loading: boolean;
  copyrightIssues?: CopyrightIssue[];
  loadingCopyright: boolean;
};

type Feedback = FeedbackRatings & {
  _id: string;
  bookID: string;
  userID: string;
  feedbackText?: string;
  language: string;
  resolved?: boolean;
  book?: {
    _id: string;
    title: string;
    author?: string;
    language: string;
    metrics?: {
      words?: number;
    };
  };
  user?: {
    email: string;
    username: string;
    displayName?: string;
  };
};

type CopyrightIssue = {
  _id: string;
  bookID: string;
  result: {
    likelyCopyrighted: boolean;
    confidence: "high" | "medium" | "low";
    reasoning: string;
  };
  book?: {
    _id: string;
    title: string;
    author?: string;
    language: string;
    metrics?: {
      words?: number;
    };
    hide?: boolean;
  };
};

export default class AdminBookFeedbackView extends preact.Component<
  Props,
  State
> {
  constructor(props: Props) {
    super(props);

    this.state = {
      feedback: undefined,
      showResolved: false,
      loading: false,
      copyrightIssues: undefined,
      loadingCopyright: false,
    };
  }

  async componentDidMount() {
    // Load both lists even if one fails (a terminal failure has already
    // shown the global notification bar)
    try {
      await this.fetchFeedback();
    } catch (error) {
      console.error("Error fetching feedback:", error);
    }
    try {
      await this.fetchCopyrightIssues();
    } catch (error) {
      console.error("Error fetching copyright issues:", error);
    }
  }

  async fetchFeedback() {
    this.setState({ loading: true });

    try {
      const data = (
        await readlangClient.request("GET", "/api/bookFeedback/admin/list", {
          showResolved: this.state.showResolved,
        })
      ).data;
      this.setState({ feedback: data.feedback });
    } finally {
      this.setState({ loading: false });
    }
  }

  async fetchCopyrightIssues() {
    this.setState({ loadingCopyright: true });

    try {
      const data = (
        await readlangClient.request("GET", "/api/copyright/admin/list", {})
      ).data;
      this.setState({ copyrightIssues: data.copyrightIssues });
    } finally {
      this.setState({ loadingCopyright: false });
    }
  }

  async handleHideBook(bookID: string) {
    try {
      await readlangClient.request(
        "PUT",
        `/api/copyright/admin/${bookID}/hide`,
        {}
      );
      // Refresh the copyright issues list
      await this.fetchCopyrightIssues();
    } catch (error: any) {
      console.error("Error hiding book:", error);
      if (!error?.jqXHR?.alreadyNotified) {
        alert("Failed to hide book");
      }
    }
  }

  async handleKeepBook(bookID: string) {
    try {
      await readlangClient.request(
        "PUT",
        `/api/copyright/admin/${bookID}/keep`,
        {}
      );
      // Refresh the copyright issues list
      await this.fetchCopyrightIssues();
    } catch (error: any) {
      console.error("Error keeping book:", error);
      if (!error?.jqXHR?.alreadyNotified) {
        alert("Failed to mark book as reviewed");
      }
    }
  }

  async toggleResolved(feedbackID: string, currentResolved: boolean) {
    try {
      await readlangClient.request(
        "PUT",
        `/api/bookFeedback/admin/${feedbackID}/resolve`,
        {
          resolved: !currentResolved,
        }
      );

      // Refresh the list
      await this.fetchFeedback();
    } catch (error: any) {
      console.error("Error toggling resolved status:", error);
      if (!error?.jqXHR?.alreadyNotified) {
        alert("Failed to update feedback status");
      }
    }
  }

  renderFeedback(feedback: Feedback) {
    const timestamp = new Date(
      parseInt(feedback._id.substring(0, 8), 16) * 1000
    );

    return (
      <tr key={feedback._id} class={cn({ resolved: feedback.resolved })}>
        <td>
          {timestamp.toLocaleDateString()} {timestamp.toLocaleTimeString()}
        </td>
        <td>
          {feedback.user ? (
            <div>
              <div>{feedback.user.email}</div>
              <div class="secondaryText">{feedback.userID}</div>
            </div>
          ) : (
            feedback.userID
          )}
        </td>
        <td>
          {feedback.book ? (
            <div>
              <a href={`/admin/book/${feedback.bookID}`}>
                {feedback.book.title}
              </a>
              {feedback.book.author && (
                <div class="secondaryText">by {feedback.book.author}</div>
              )}
              <div class="tertiaryText">{feedback.language}</div>
              {feedback.book.metrics?.words !== undefined && (
                <div class="tertiaryText">
                  {feedback.book.metrics?.words.toLocaleString()} words
                </div>
              )}
            </div>
          ) : (
            <div>
              <a href={`/admin/book/${feedback.bookID}`}>{feedback.bookID}</a>
            </div>
          )}
        </td>
        <AdminFeedbackRatingCells feedback={feedback} />
        <td>{feedback.feedbackText || <em class="emptyText">None</em>}</td>
        <td class="center">
          <button
            onClick={() =>
              this.toggleResolved(feedback._id, feedback.resolved || false)
            }
            class={cn({
              prominentGreen: !feedback.resolved,
            })}
          >
            {feedback.resolved ? "Mark Unresolved" : "Mark Resolved"}
          </button>
        </td>
      </tr>
    );
  }

  renderCopyrightIssue(issue: CopyrightIssue) {
    const timestamp = new Date(parseInt(issue._id.substring(0, 8), 16) * 1000);

    return (
      <tr key={issue._id}>
        <td>
          {timestamp.toLocaleDateString()} {timestamp.toLocaleTimeString()}
        </td>
        <td>
          {issue.book ? (
            <div>
              <a href={`/admin/book/${issue.bookID}`}>{issue.book.title}</a>
              {issue.book.author && (
                <div class="secondaryText">by {issue.book.author}</div>
              )}
              <div class="tertiaryText">{issue.book.language}</div>
              {issue.book.metrics?.words !== undefined && (
                <div class="tertiaryText">
                  {issue.book.metrics?.words.toLocaleString()} words
                </div>
              )}
              {issue.book.hide && (
                <div class="tertiaryText">(Currently hidden)</div>
              )}
            </div>
          ) : (
            <div>
              <a href={`/admin/book/${issue.bookID}`}>{issue.bookID}</a>
            </div>
          )}
        </td>
        <td class="center">{issue.result.confidence}</td>
        <td>{issue.result.reasoning}</td>
        <td class="center">
          <button
            onClick={() => this.handleHideBook(issue.bookID)}
            class="prominentRed"
          >
            Hide from Library
          </button>
          <button onClick={() => this.handleKeepBook(issue.bookID)}>
            Keep Visible
          </button>
        </td>
      </tr>
    );
  }

  render() {
    return (
      <div>
        <h1>
          <a href="/admin">Admin</a> - Book Feedback
        </h1>

        <div class="controls">
          <label>
            <input
              type="checkbox"
              checked={this.state.showResolved}
              onChange={async (event) => {
                await this.setState({
                  showResolved: event.currentTarget.checked,
                });
                await this.fetchFeedback();
              }}
            />
            Show resolved feedback
          </label>
          <button onClick={() => this.fetchFeedback()}>Refresh</button>
        </div>

        {this.state.loadingCopyright ? (
          <p>Loading copyright issues...</p>
        ) : this.state.copyrightIssues !== undefined ? (
          <div>
            <h2>Copyright Issues ({this.state.copyrightIssues.length})</h2>
            {this.state.copyrightIssues.length === 0 ? (
              <p>No outstanding copyright issues</p>
            ) : (
              <table class="feedbackTable">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Book</th>
                    <th>Confidence</th>
                    <th>Reasoning</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {this.state.copyrightIssues.map((issue) =>
                    this.renderCopyrightIssue(issue)
                  )}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          <p>Loading...</p>
        )}

        {this.state.loading ? (
          <p>Loading...</p>
        ) : this.state.feedback !== undefined ? (
          <div>
            <h2>
              {this.state.showResolved ? "All" : "Unresolved"} Feedback (
              {this.state.feedback.length})
            </h2>
            {this.state.feedback.length === 0 ? (
              <p>No feedback found</p>
            ) : (
              <table class="feedbackTable">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>User</th>
                    <th>Book</th>
                    <th>Content</th>
                    <th>Language</th>
                    <th>Feedback</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {this.state.feedback.map((feedback) =>
                    this.renderFeedback(feedback)
                  )}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          <p>Loading...</p>
        )}
      </div>
    );
  }
}
